/**
 * Governance Proposals Tool — MCP surface for consent proposal lifecycle.
 *
 * Chain: BET-221 (Governance at Scale), BR-7 (async consent required),
 *        TEN-967 (consent proposals only)
 *
 * Actions:
 *   list    — List open consent proposals with expiry countdown
 *   respond — Approve or reject a proposal (with reason for rejections)
 *   count   — Count open proposals (for badge/notification)
 */

import { kernelQuery, kernelMutation, requireWriteAccess } from "../client.js";
import { successResult, failureResult, validationResult } from "../envelope.js";

/**
 * WP-484 S2 (§3): extracted governance-proposals handlers — reused by the
 * legacy `governance-proposals` standalone tool AND `workspace
 * action=proposals-list/respond/count` (absorbed per §3 core-nouns table).
 * One business-logic path per action.
 */
export async function handleProposalsList(status?: "open" | "approved" | "objected" | "expired") {
  const proposals = await kernelQuery<any[]>("governance.listProposals", {
    ...(status ? { status } : {}),
  });

  if (proposals.length === 0) {
    const statusLabel = status ?? "open";
    return successResult(
      `No ${statusLabel} consent proposals in this workspace.`,
      `No ${statusLabel} consent proposals found.`,
      { proposals: [], count: 0 },
    );
  }

  const lines: string[] = ["# Consent Proposals", ""];

  for (const p of proposals) {
    const urgency = p.isExpired
      ? "EXPIRED"
      : p.hoursRemaining < 1
        ? `< 1h remaining`
        : `${p.hoursRemaining}h remaining`;

    lines.push(`## ${p.targetEntryName} → ${p.toEntryName ?? "unknown"}`);
    lines.push(`- **ID:** \`${p._id}\``);
    lines.push(`- **Status:** ${p.status} (${urgency})`);
    lines.push(`- **Operation:** ${p.proposedOperation}`);
    lines.push(`- **Reasoning:** ${p.reasoning}`);
    lines.push(`- **Proposed by:** ${p.proposedBy}`);
    if (p.objectionReason) {
      lines.push(`- **Objection:** ${p.objectionReason}`);
    }
    lines.push("");
  }

  return successResult(
    lines.join("\n"),
    `Found ${proposals.length} consent proposal(s).`,
    { proposals, count: proposals.length },
    [
      {
        tool: "workspace",
        description: "Respond to a proposal",
        parameters: { action: "proposals-respond" },
      },
    ],
  );
}

export async function handleProposalsCount() {
  const result = await kernelQuery<{ count: number }>("governance.countOpenProposals");

  return successResult(
    result.count === 0
      ? "No open consent proposals."
      : `${result.count} open consent proposal(s) awaiting review.`,
    `${result.count} open consent proposal(s).`,
    result,
    result.count > 0
      ? [{
          tool: "workspace",
          description: "List open proposals",
          parameters: { action: "proposals-list" },
        }]
      : undefined,
  );
}

export async function handleProposalsRespond(proposalId: string, verdict: "approve" | "reject", reason?: string) {
  requireWriteAccess();
  if (verdict === "reject" && !reason) {
    return validationResult(
      "A reason is required when rejecting a proposal. Explain what harm this change would cause.",
    );
  }

  try {
    const result = await kernelMutation<{ status: string; proposalId: string; message: string }>(
      "governance.respondToProposal",
      {
        proposalId,
        verdict,
        ...(reason ? { reason } : {}),
      },
    );

    return successResult(
      result.message,
      result.message,
      result,
      [
        {
          tool: "workspace",
          description: "List remaining proposals",
          parameters: { action: "proposals-list" },
        },
      ],
    );
  } catch (err: any) {
    const msg = err?.message ?? String(err);
    return failureResult(
      msg,
      "PROPOSAL_ERROR",
      msg,
      "Use workspace action=proposals-list to check proposal status.",
      [
        {
          tool: "workspace",
          description: "List proposals",
          parameters: { action: "proposals-list" },
        },
      ],
    );
  }
}

// WP-484 S3 (clean break, TEN-2157): the standalone `governance-proposals`
// tool retired here. handleProposals* stay live — workspace.ts (the
// `workspace` compound, action=proposals-list/respond/count) is their only
// remaining caller.
